import { DatePipe } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';

import { take, tap } from 'rxjs/operators';

import { ButtonModule } from 'primeng/button';
import { DropdownModule } from 'primeng/dropdown';

import { AgendaCRUDService, AgendaReadOnlySchema } from '@digitalforest/ngx-oak-api';

export interface AgendaMenuItem extends AgendaReadOnlySchema {
  label: string;
  labelPrefix: string;
  timeString: string;
  dateString: string;
}

export interface RoomMenuItem {
  label: string;
  value: string;
}

@Component({
  selector: 'app-agenda-list-page',
  templateUrl: './agenda-list-page.component.html',
  styleUrls: ['./agenda-list-page.component.scss'],
  standalone: true,
  imports: [DropdownModule, FormsModule, ButtonModule],
})
export class AgendaListPageComponent implements OnInit {
  @ViewChild('roomFilterDropdown') roomFilterDropdown!: DropdownModule;

  agendaList: AgendaReadOnlySchema[] = [];
  agendaMenuItemList: AgendaMenuItem[] = [];
  roomMenuList: RoomMenuItem[] = [];
  roomFilter: string | null = null;
  isLoading = true;

  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private agendaCRUDService = inject(AgendaCRUDService);

  private companyId = parseInt(this.route.snapshot.paramMap.get('company_id') || '0');
  private eventId = parseInt(this.route.snapshot.paramMap.get('event_id') || '0');

  ngOnInit(): void {
    const agendaObservable =
      this.eventId === 0
        ? this.agendaCRUDService.manageGetExhibitorAgendaListForCheckIn(this.companyId)
        : this.agendaCRUDService.manageGetAllAgendasByEvent(this.companyId, this.eventId);

    agendaObservable
      .pipe(
        take(1),
        tap((agendas) => {
          this.agendaList = agendas
            .filter((agenda: AgendaReadOnlySchema) => agenda.active && agenda.bookable)
            .sort((a, b) => new Date(a.session_begin || '').getTime() - new Date(b.session_begin || '').getTime());

          const uniqueSessionDataList = Array.from(new Set(this.agendaList.map((agenda: AgendaReadOnlySchema) => agenda.session_begin)));

          // Since session_begin could be undefined, we need to handle that
          const validSessionDataList = uniqueSessionDataList.filter((date) => date !== undefined).map((date) => date as string | Date); // It's actually string from API, but we keep the type

          const compatibleList: (string | Date | null)[] = validSessionDataList;

          this.roomMenuList = [];
          this.agendaMenuItemList = this.processAgendaList(this.agendaList, compatibleList);
          this.isLoading = false;
        }),
      )
      .subscribe({
        error: () => {
          this.isLoading = false;
        },
      });
  }

  private processAgendaList(agendaList: AgendaReadOnlySchema[], uniqueSessionDataList: (string | Date | null)[]): AgendaMenuItem[] {
    const datePipe = new DatePipe('en-US');

    return agendaList
      .map((agenda: AgendaReadOnlySchema) => {
        const label = agenda.title || '';
        let labelPrefix = '';

        if (agenda.session_begin) {
          const formattedTime = datePipe.transform(agenda.session_begin, 'HH:mm');
          labelPrefix = `${formattedTime}`;
        }

        if (uniqueSessionDataList.length > 1) {
          const formattedDate = datePipe.transform(agenda.session_begin, 'dd/MM');
          labelPrefix = `${formattedDate} ${labelPrefix}`;
        }

        this.updateRoomMenuList(agenda);

        return {
          ...agenda,
          label: label,
          labelPrefix: labelPrefix,
          timeString: datePipe.transform(agenda.session_begin, 'HH:mm') || '',
          dateString: datePipe.transform(agenda.session_begin, 'yyyy-MM-dd') || '',
        };
      })
      .sort((a, b) => {
        const dateComparison = a.dateString.localeCompare(b.dateString);
        return dateComparison !== 0 ? dateComparison : a.timeString.localeCompare(b.timeString);
      });
  }

  private updateRoomMenuList(agenda: AgendaReadOnlySchema): void {
    const roomMenuItem = {
      label: agenda.room || '',
      value: agenda.room || '',
    };

    if (!this.roomMenuList.some((item) => item.label === roomMenuItem.label && item.value === roomMenuItem.value)) {
      this.roomMenuList.push(roomMenuItem);
    }
  }

  onSelectEvent() {
    if (this.eventId === 0 && this.agendaList.length > 0) {
      const firstAgenda = this.agendaList[0];
      this.router.navigate(['/', 'main', 'scan', 'agenda', this.companyId, firstAgenda.event_id, firstAgenda.id]).then();
    } else {
      this.router.navigate(['/', 'main', 'scan', 'event', this.companyId, this.eventId]).then();
    }
  }

  onSelectAgenda(agendaId: number) {
    const selectedAgenda = this.agendaList.find((agenda) => agenda.id === agendaId);
    if (selectedAgenda) {
      this.router.navigate(['/', 'main', 'scan', 'agenda', this.companyId, selectedAgenda.event_id, agendaId]).then();
    }
  }
}
